import * as React from "react";
import { useState, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Stack,
  Avatar,
  Fade,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// Use env variables for API secret and URL
const API_SECRET = process.env.REACT_APP_API_SECRET;
const API_URL = process.env.REACT_APP_API_URL as string;
const PREVIEW_URL = require("../../../media/apps/petTestPhoto.jpg"); // Placeholder image

const PetClassifier = () => {
  const [selectedImg, setSelectedImg] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(PREVIEW_URL);
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasClassified, setHasClassified] = useState<boolean>(false);

  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedImg(file);
    setPreviewUrl(file ? URL.createObjectURL(file) : PREVIEW_URL);
    setResult(null);
    setHasClassified(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedImg) return;
    setLoading(true);
    setResult(null);

    const formData = new FormData();
    formData.append("file", selectedImg);

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: new Headers({
          "X-API-SECRET": API_SECRET ?? "",
        }),
        body: formData,
      });
      const data = await response.json();
      setResult(data);
      setHasClassified(true);
    } catch (err) {
      setResult({ error: "Failed to contact server." });
      setHasClassified(true);
    }
    setLoading(false);
  };

  const handleCameraButtonClick = () => {
    cameraInputRef.current?.click();
  };

  return (
    <Box
      sx={{
        maxWidth: 370,
        mx: "auto",
        mt: 4,
      }}
    >
      <Card variant="outlined" className="customCard">
        <CardContent>
          <Typography variant="h5" align="center" gutterBottom>
            Pet Classifier
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, mb: 1 }}>
            {/* Hidden file inputs */}
            <input
              type="file"
              accept="image/*"
              id="main-file-input"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <input
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              ref={cameraInputRef}
              onChange={handleFileChange}
            />

            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              sx={{ mb: 2 }}
            >
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => {
                  const input = document.getElementById("main-file-input");
                  if (input) {
                    input.click();
                  }
                }}
              >
                File
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={handleCameraButtonClick}
              >
                Camera
              </Button>
            </Stack>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              type="submit"
              disabled={!selectedImg || loading || hasClassified}
              sx={{ minHeight: 40, fontWeight: 600 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Guess"
              )}
            </Button>
          </Box>

          <Fade in={!!previewUrl}>
            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
              {previewUrl && (
                <Avatar
                  variant="rounded"
                  src={previewUrl}
                  alt="preview"
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    border: "1px solid #ccc",
                  }}
                />
              )}
            </Box>
          </Fade>

          {result && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              {result.error ? (
                <Typography color="error">{result.error}</Typography>
              ) : (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Prediction:{" "}
                    <span style={{ color: "#1976d2" }}>
                      {result.prediction
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                    </span>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Probability: {(result.probability * 100).toFixed(1)}%
                  </Typography>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PetClassifier;
